import React, { useEffect, useImperativeHandle, forwardRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Markdown } from 'tiptap-markdown';
import { Bold, Italic, Heading2, List, ListOrdered, CheckSquare, Table as TableIcon, Link as LinkIcon } from 'lucide-react';

/*
 * A note is stored as Markdown (GitHub style: tables, task lists, headings), so it pastes in from Claude,
 * Notion or Google Docs with its formatting, and copies or downloads out as a clean .md file.
 * To-dos are saved as "- [ ] …" lines, which is what "Make tasks" on the server looks for.
 */

// Notes written before Markdown were plain text: one thought per line, to-dos as "[ ] …".
// Turn them into Markdown that reads the same: each line its own paragraph, to-do lines a task list.
export { tidy as tidyMarkdown };
export const legacyToMarkdown = (text = '') => {
  const lines = text.split('\n').map((l) => l.replace(/^(\s*)\[(\s?|x|X|→)\]\s+/, (m, ind, k) => `${ind}- [${k.trim() ? k : ' '}] `));
  const isItem = (l) => /^\s*([-*+]|\d+\.)\s/.test(l);
  let out = '';
  lines.forEach((l, i) => {
    if (i === 0) { out = l; return; }
    out += isItem(l) && isItem(lines[i - 1]) ? `\n${l}` : `\n\n${l}`;
  });
  return out;
};

// The serializer escapes the "[→]" that marks a to-do already made into a task; keep it readable.
// Keep lists tight (no blank line between items), as Notion and most Markdown tools write them.
const ITEM = /^\s*(?:[-*+]|\d+\.)\s/;
const tighten = (md) => md.split('\n').filter((l, i, a) => !(l === '' && ITEM.test(a[i - 1] || '') && ITEM.test(a[i + 1] || ''))).join('\n');
const tidy = (md) => tighten(md.replace(/\\\[→\\\]/g, '[→]').replace(/^(\s*[-*+] \[[ xX→]\]) {2,}/gm, '$1 ')).replace(/\n{3,}/g, '\n\n').trim();

const Tool = ({ on, onClick, title, children }) => (
  <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
    className={`h-7 w-7 inline-flex items-center justify-center rounded ${on ? 'bg-[#1E2A33] text-white' : 'text-[#5C6670] hover:bg-[#F3EFE7]'}`}>
    {children}
  </button>
);

const MarkdownNoteEditor = forwardRef(function MarkdownNoteEditor({ markdown, onChange, onBlur, placeholder, autoFocus }, ref) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder }),
      Table.configure({ resizable: false }), TableRow, TableHeader, TableCell,
      TaskList, TaskItem.configure({ nested: true }),
      Markdown.configure({ html: false, tightLists: true, linkify: true, breaks: false, transformPastedText: true, transformCopiedText: false }),
    ],
    content: markdown || '',
    autofocus: autoFocus ? 'end' : false,
    onUpdate: ({ editor: e }) => onChange?.(tidy(e.storage.markdown.getMarkdown())),
    onBlur: () => onBlur?.(),
    editorProps: { attributes: { class: 'note-md prose-note outline-none min-h-[120px] text-sm text-[#1E2A33] leading-relaxed' } },
  });

  useImperativeHandle(ref, () => ({
    // Replace the whole note (after "Make tasks" rewrites it on the server) without firing a save.
    setMarkdown: (md) => editor?.commands.setContent(md || '', false),
    addTodo: () => editor?.chain().focus('end').toggleTaskList().run(),
  }), [editor]);

  useEffect(() => () => editor?.destroy(), [editor]);
  if (!editor) return null;
  const c = () => editor.chain().focus();
  const addLink = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Link address', prev); // eslint-disable-line no-alert
    if (url === null) return;
    if (!url) c().unsetLink().run(); else c().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div>
      <div className="flex items-center gap-0.5 mb-1.5 -ml-1 flex-wrap">
        <Tool title="Bold" on={editor.isActive('bold')} onClick={() => c().toggleBold().run()}><Bold className="w-3.5 h-3.5" /></Tool>
        <Tool title="Italic" on={editor.isActive('italic')} onClick={() => c().toggleItalic().run()}><Italic className="w-3.5 h-3.5" /></Tool>
        <Tool title="Heading" on={editor.isActive('heading', { level: 2 })} onClick={() => c().toggleHeading({ level: 2 }).run()}><Heading2 className="w-3.5 h-3.5" /></Tool>
        <Tool title="Bulleted list" on={editor.isActive('bulletList')} onClick={() => c().toggleBulletList().run()}><List className="w-3.5 h-3.5" /></Tool>
        <Tool title="Numbered list" on={editor.isActive('orderedList')} onClick={() => c().toggleOrderedList().run()}><ListOrdered className="w-3.5 h-3.5" /></Tool>
        <Tool title="To-do list" on={editor.isActive('taskList')} onClick={() => c().toggleTaskList().run()}><CheckSquare className="w-3.5 h-3.5" /></Tool>
        <Tool title={editor.isActive('table') ? 'Add a row' : 'Table'} on={editor.isActive('table')} onClick={() => (editor.isActive('table') ? c().addRowAfter().run() : c().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run())}><TableIcon className="w-3.5 h-3.5" /></Tool>
        <Tool title="Link" on={editor.isActive('link')} onClick={addLink}><LinkIcon className="w-3.5 h-3.5" /></Tool>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
});

export default MarkdownNoteEditor;
